import React, { Component } from "react";
import {
  Table,
  Dropdown,
  Modal,
  Input,
  message,
  Tooltip,
  Typography
} from "antd";
import { EditOutlined, DownOutlined } from "@ant-design/icons";
import moment from "moment";
import Fetch from "../../utils/fetch";
import EvAlertSearch from "../evAlertSearch";
import jwtDecode from "jwt-decode";

const { TextArea } = Input;

const ALERT_TYPE_SHORT = {
  "LOW SOC": "SOC",
  "HIGH VOLTAGE": "Voltage",
  "HIGH BATTERY TEMP": "Bat Temp",
  "HIGH MOTOR TEMP": "Motor"
};

const shortAlertType = val =>
  ALERT_TYPE_SHORT[(val || "").toUpperCase()] || val;

const STATUS_CONFIG = {
  CREATED: {
    bg: "#fff",
    color: "#434343",
    border: "1px solid #d9d9d9",
    label: "CREATED"
  },
  INPROGRESS: {
    bg: "#fff7e6",
    color: "#d46b08",
    border: "1px solid #ffa940",
    label: "IN PROGRE..."
  },
  CLOSED: {
    bg: "#f6ffed",
    color: "#389e0d",
    border: "1px solid #95de64",
    label: "CLOSED"
  }
};

const StatusDropdown = ({ value, onChange }) => {
  const cfg = STATUS_CONFIG[value] || STATUS_CONFIG.CREATED;

  // A CLOSED alert is terminal — show a static badge, no dropdown.
  if (value === "CLOSED") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 8px",
          background: cfg.bg,
          border: cfg.border,
          borderRadius: 4,
          minWidth: 110,
          color: cfg.color,
          fontSize: 12,
          fontWeight: 500,
          cursor: "default"
        }}
      >
        {cfg.label}
      </span>
    );
  }

  const overlay = (
    <div
      style={{
        background: "#2d2d2d",
        borderRadius: 8,
        boxShadow: "0 6px 16px rgba(0,0,0,0.45)",
        overflow: "hidden",
        minWidth: 130
      }}
    >
      {["CREATED", "INPROGRESS", "CLOSED"].map(s => (
        <div
          key={s}
          style={{
            padding: "8px 16px",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13
          }}
          onClick={() => onChange(s)}
        >
          <span style={{ width: 16, color: "#52c41a", fontSize: 12 }}>
            {value === s ? "✓" : ""}
          </span>
          {s}
        </div>
      ))}
    </div>
  );

  return (
    <Dropdown overlay={overlay} trigger={["click"]} placement="bottomLeft">
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          padding: "3px 8px",
          background: cfg.bg,
          border: cfg.border,
          borderRadius: 4,
          cursor: "pointer",
          minWidth: 110
        }}
      >
        <span style={{ color: cfg.color, fontSize: 12, fontWeight: 500 }}>
          {cfg.label}
        </span>
        <DownOutlined style={{ fontSize: 9, color: cfg.color }} />
      </div>
    </Dropdown>
  );
};

const formatDuration = createdOn => {
  if (!createdOn) return "--";
  const diff = Date.now() - createdOn;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  if (hrs > 0) return `${hrs}h ${mins % 60}m`;
  return `${mins} mins`;
};

class EvAlerts extends Component {
  state = {
    loading: false,
    alerts: [],
    showCloseModal: false,
    closeTarget: null,
    closeComment: "",
    showRemarksModal: false,
    remarksTarget: null,
    remarkInput: "",
    busOptions: [],
    serviceOptions: [],
    currentFilters: {
      statuses: "CREATED,INPROGRESS",
      from: new Date().setHours(0, 0, 0, 0),
      to: new Date().setHours(23, 59, 59, 999)
    }
  };

  componentDidMount() {
    // Fire alerts + filter options together on first load (all in parallel).
    Promise.all([
      this.fetchAlerts(this.state.currentFilters),
      this.loadOptions()
    ]);
  }

  loadOptions = async () => {
    try {
      const [buses, services] = await Promise.all([
        Fetch("/ev-trips/getBusRc", { method: "get" }),
        Fetch("/ev-trips/getService", { method: "get" })
      ]);
      this.setState({
        busOptions: Array.isArray(buses)
          ? buses.map(b => b.rc).filter(Boolean)
          : [],
        serviceOptions: Array.isArray(services)
          ? services.filter(s => s && s._id && s.name)
          : []
      });
    } catch (err) {
      // non-fatal: fields still work as free text
    }
  };

  fetchAlerts = async (filters = {}) => {
    this.setState({ loading: true });
    try {
      const params = new URLSearchParams();
      if (filters.statuses) params.append("statuses", filters.statuses);
      if (filters.from) params.append("from", filters.from);
      if (filters.to) params.append("to", filters.to);
      if (filters.busRc) params.append("busRc", filters.busRc);
      if (filters.alertType) params.append("alertType", filters.alertType);
      if (filters.closureType)
        params.append("closureType", filters.closureType);
      if (filters.route) params.append("route", filters.route);

      const url = `/ev-trips/alerts?${params.toString()}`;
      const response = await Fetch(url, { method: "get" });

      if (response) {
        const alerts = Array.isArray(response) ? response : [];
        this.setState({ alerts, loading: false });
      } else {
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch alerts");
      this.setState({ loading: false });
    }
  };

  onSearch = filters => {
    this.setState({ currentFilters: filters });
    this.fetchAlerts(filters);
  };

  onRefresh = () => {
    this.fetchAlerts(this.state.currentFilters);
  };

  // Patch a single alert in local state instead of re-fetching the whole list.
  updateAlertInState = (id, patch) => {
    this.setState(prev => ({
      alerts: prev.alerts.map(a => (a._id === id ? { ...a, ...patch } : a))
    }));
  };

  handleStatusChange = async (alertId, newStatus, alert) => {
    if (newStatus === "CLOSED") {
      this.setState({
        showCloseModal: true,
        closeTarget: alert,
        closeComment: ""
      });
      return;
    }

    try {
      const url = `/ev-trips/alerts/${alertId}/status`;
      const response = await Fetch(url, {
        method: "post",
        data: { status: newStatus }
      });
      if (response && Array.isArray(response)) {
        message.success("Status updated");
        this.updateAlertInState(alertId, {
          status: newStatus,
          lastModifiedOn: Date.now()
        });
      } else {
        message.error("Failed to update status");
      }
    } catch (err) {
      message.error("Failed to update status");
    }
  };

  handleCloseConfirm = async () => {
    const { closeTarget, closeComment } = this.state;
    if (!closeComment || closeComment.trim().length === 0) {
      message.error("Please enter a comment to close the alert");
      return;
    }

    try {
      const url = `/ev-trips/alerts/${closeTarget._id}/close`;
      const response = await Fetch(url, {
        method: "post",
        data: {
          comment: closeComment.trim(),
          busRc: closeTarget.busRc,
          tripId: closeTarget.tripId,
          alertType: closeTarget.type
        }
      });
      if (response && Array.isArray(response)) {
        message.success("Alert closed");
        const now = Date.now();
        this.updateAlertInState(closeTarget._id, {
          status: "CLOSED",
          closureType: "Manual",
          resolvedAt: now,
          lastModifiedOn: now,
          closeComment: closeComment.trim()
        });
        this.setState({
          showCloseModal: false,
          closeTarget: null,
          closeComment: ""
        });
      } else {
        message.error("Failed to close alert");
      }
    } catch (err) {
      message.error("Failed to close alert");
    }
  };

  openRemarksModal = alert => {
    this.setState({
      showRemarksModal: true,
      remarksTarget: alert,
      remarkInput: ""
    });
  };

  handleAddRemark = async () => {
    const { remarksTarget, remarkInput } = this.state;
    if (!remarkInput || remarkInput.trim().length === 0) {
      message.error("Please enter a remark");
      return;
    }
    const token = localStorage.getItem("idToken");
    const decodedToken = jwtDecode(token);
    const text = remarkInput.trim();
    const entry = `${text} (added by ${
      decodedToken.name
    } at ${new Date().toLocaleString()})`;

    try {
      const url = `/ev-trips/alerts/${remarksTarget._id}/remarks`;
      const response = await Fetch(url, {
        method: "post",
        data: { text, oldRemarks: remarksTarget.remarks || "" }
      });
      if (response && Array.isArray(response)) {
        message.success("Remark added");
        this.setState(prev => ({
          showRemarksModal: false,
          remarksTarget: null,
          remarkInput: "",
          alerts: prev.alerts.map(a =>
            a._id === remarksTarget._id
              ? {
                  ...a,
                  remarks: a.remarks ? `${a.remarks}||${entry}` : entry
                }
              : a
          )
        }));
      } else {
        message.error("Failed to add remark");
      }
    } catch (err) {
      message.error("Failed to add remark");
    }
  };

  getColumns = () => [
    {
      title: "Time Stamp",
      dataIndex: "createdOn",
      key: "createdOn",
      width: 130,
      render: val => (val ? moment(val).format("DD MMM, HH:mm") : "--")
    },
    {
      title: "Bus RC",
      dataIndex: "busRc",
      key: "busRc",
      width: 110
    },
    {
      title: "Trip ID",
      dataIndex: "tripId",
      key: "tripId",
      width: 250,
      render: val => {
        if (!val) return "--";
        const str = String(val);
        return (
          <Typography.Text copyable={{ text: str }}>{str}</Typography.Text>
        );
      }
    },
    {
      title: "Service Name",
      key: "serviceName",
      width: 200,
      render: (_, record) =>
        (record.trip && record.trip.service && record.trip.service.name) || "--"
    },
    {
      title: "Alert Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: val => (
        <span style={{ color: "#ff4d4f", fontWeight: 500 }}>
          {shortAlertType(val)}
        </span>
      )
    },
    {
      title: "Value",
      key: "value",
      width: 70,
      align: "center",
      render: (_, record) => {
        const val = record.metadata && record.metadata.value;
        return (
          <span style={{ color: val != null ? "#ff4d4f" : "inherit" }}>
            {val != null ? val : "--"}
          </span>
        );
      }
    },
    {
      title: "Threshold",
      key: "threshold",
      width: 90,
      align: "center",
      render: (_, record) =>
        (record.metadata && record.metadata.threshold) || "--"
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 100,
      render: (remarks, record) => {
        const addLink = (
          <span
            style={{
              color: "#1890ff",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 13
            }}
            onClick={() => this.openRemarksModal(record)}
          >
            <EditOutlined style={{ fontSize: 12 }} />
            Add
          </span>
        );

        const list = (remarks || "")
          .split("||")
          .map(s => s.trim())
          .filter(Boolean);

        if (list.length === 0) return addLink;

        return (
          <Tooltip
            title={list.map((r, i) => (
              <div key={i}>{r}</div>
            ))}
          >
            {addLink}
          </Tooltip>
        );
      }
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status, record) => (
        <StatusDropdown
          value={status}
          onChange={val => this.handleStatusChange(record._id, val, record)}
        />
      )
    },
    {
      title: "Resolution",
      key: "closureType",
      width: 90,
      render: (_, record) =>
        (record.metadata && record.metadata.closureType) ||
        record.closureType ||
        "-"
    },
    {
      title: "Resolved by",
      key: "resolvedBy",
      width: 110,
      render: (_, record) =>
        record.resolvedBy || (record.closedBy && record.closedBy.name) || "-"
    },
    {
      title: "Resolved At",
      key: "resolvedAt",
      width: 130,
      render: (_, record) =>
        record.status === "CLOSED" && record.closedOn
          ? moment(record.closedOn).format("DD MMM, HH:mm")
          : "--"
    },
    {
      title: "Duration",
      key: "duration",
      width: 90,
      render: (_, record) => {
        const { createdOn } = record;
        if (!createdOn) return "--";
        if (record.status === "CLOSED") {
          const end = record.resolvedAt || record.lastModifiedOn || Date.now();
          const mins = Math.floor((end - createdOn) / 60000);
          const hrs = Math.floor(mins / 60);
          return hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins} mins`;
        }
        return formatDuration(createdOn);
      }
    }
  ];

  render() {
    const {
      loading,
      alerts,
      showCloseModal,
      closeComment,
      showRemarksModal,
      remarkInput
    } = this.state;

    return (
      <div style={{ padding: "0 16px" }}>
        <EvAlertSearch
          onSearch={this.onSearch}
          onRefresh={this.onRefresh}
          busOptions={this.state.busOptions}
          serviceOptions={this.state.serviceOptions}
        />

        <Table
          columns={this.getColumns()}
          dataSource={alerts}
          rowKey={r => r._id}
          loading={loading}
          pagination={false}
          scroll={{ x: 1300, y: "60vh" }}
          size="small"
        />

        <Modal
          title="Resolve Alert"
          visible={showCloseModal}
          onOk={this.handleCloseConfirm}
          onCancel={() =>
            this.setState({
              showCloseModal: false,
              closeTarget: null,
              closeComment: ""
            })
          }
          okText="Confirm"
        >
          <p>
            Are you sure you want to resolve this alert? It will not re-fire
            unless the value returns to normal and breaches again.
          </p>
          <TextArea
            rows={3}
            placeholder="Enter reason / comment (required)"
            value={closeComment}
            onChange={e => this.setState({ closeComment: e.target.value })}
          />
        </Modal>

        <Modal
          title="Add Remark"
          visible={showRemarksModal}
          onOk={this.handleAddRemark}
          onCancel={() =>
            this.setState({
              showRemarksModal: false,
              remarksTarget: null,
              remarkInput: ""
            })
          }
          okText="Add"
        >
          <TextArea
            rows={3}
            placeholder="Enter remark"
            value={remarkInput}
            onChange={e => this.setState({ remarkInput: e.target.value })}
          />
        </Modal>
      </div>
    );
  }
}

export default EvAlerts;
